import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsAPI.list({ search, page, limit: 50 });
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const scheduleColor = (s) => {
    const m = { H: 'badge-red', H1: 'badge-orange', X: 'badge-purple', G: 'badge-yellow', OTC: 'badge-green', none: 'badge-gray' };
    return m[s] || 'badge-gray';
  };

  return (
    <div>
      <div className="page-header">
        <div><h1>Products</h1><p className="subtitle">{total} products in catalog</p></div>
        <Link to="/products/new" className="btn btn-primary">+ Add Product</Link>
      </div>

      <div className="card">
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="search-bar">
            <div className="search-input-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Search by name, SKU, generic name, barcode..."
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
          </div>
        </div>
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner" /></div> : (
            <table>
              <thead>
                <tr><th>Product</th><th>SKU</th><th>HSN</th><th>Schedule</th><th>Pack</th><th style={{textAlign:'right'}}>MRP</th><th style={{textAlign:'right'}}>GST%</th><th style={{textAlign:'right'}}>Stock</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.name}</div>
                      {p.generic_name && <div className="text-xs text-secondary">{p.generic_name}</div>}
                      {p.manufacturer_name && <div className="text-xs text-secondary">{p.manufacturer_name}</div>}
                    </td>
                    <td className="td-mono">{p.sku}</td>
                    <td className="td-mono">{p.hsn_code}</td>
                    <td><span className={`badge ${scheduleColor(p.drug_schedule)}`}>{p.drug_schedule === 'none' ? 'General' : `Sch. ${p.drug_schedule}`}</span></td>
                    <td className="text-secondary">{p.pack_size}</td>
                    <td className="td-amount">₹{Number(p.mrp).toFixed(2)}</td>
                    <td className="td-amount">{p.gst_rate}%</td>
                    <td className="td-amount" style={{ color: Number(p.total_stock) <= Number(p.reorder_level) ? 'var(--danger)' : 'inherit', fontWeight: Number(p.total_stock) <= Number(p.reorder_level) ? 700 : 400 }}>
                      {p.total_stock || 0}
                    </td>
                    <td>
                      <Link to={`/products/${p.id}/edit`} className="btn btn-outline btn-sm">Edit</Link>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr><td colSpan={9} className="empty-state"><h3>No products found</h3><p>Add your first product to get started</p></td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {total > 50 && (
          <div className="card-body flex items-center justify-between">
            <span className="text-sm text-secondary">Showing {(page-1)*50+1}-{Math.min(page*50, total)} of {total}</span>
            <div className="flex gap-2">
              <button className="btn btn-outline btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>Previous</button>
              <button className="btn btn-outline btn-sm" disabled={page*50 >= total} onClick={() => setPage(p=>p+1)}>Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
